import * as THREE from 'three';
import { World, setupSky, SUN_OFFSET } from '../world/World';
import { Input } from '../player/Input';
import { PlayerController } from '../player/PlayerController';
import { Hotbar } from '../building/Hotbar';
import { BuildingSystem } from '../building/BuildingSystem';
import {
  CraftingSystem,
  type CraftFaceAction,
} from '../building/CraftingSystem';
import { ToolsPalette } from '../building/ToolsPalette';
import {
  TRANSFORM_FACE_UI,
  TRANSFORM_FACE_UI_ID,
  transformActionForZone,
} from '../building/TransformFaceUi';
import {
  CRAFT_FACE_UI,
  CRAFT_FACE_UI_ID,
} from '../building/CraftFaceUi';
import { FaceUiSystem } from '../ui/FaceUi';
import { Connection, InitMsg } from '../net/Connection';
import { RemotePlayers } from './RemotePlayers';
import { Signs } from './Signs';
import { showToast } from '../ui/toast';
import { api, type BlockLibrary, type Profile } from '../net/api';
import { DevPanel } from '../debug/DevPanel';
import {
  RenderSettings,
  type RenderSettingsState,
} from '../debug/RenderSettings';
import {
  CRAFT_BLOCK_TYPE,
  currentWorldAnimTick,
  customBlockId,
  libraryBlockId,
  type ServerMsg,
} from '@shared/protocol';

/** How often the local player state is sent to the server. */
const MOVE_SEND_INTERVAL = 0.1;
const TEXTURE_DRAFT_FACE_TYPE = -100;

/** The in-world game: rendering, controls, and real-time server sync. */
export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly sun: THREE.DirectionalLight;
  private readonly world = new World();
  private readonly input: Input;
  private readonly player: PlayerController;
  private readonly hotbar: Hotbar;
  private readonly building: BuildingSystem;
  private readonly crafting: CraftingSystem;
  private readonly tools: ToolsPalette;
  private readonly faceUi: FaceUiSystem;
  private readonly remotes = new RemotePlayers();
  private readonly signs: Signs;
  private readonly clock = new THREE.Clock();
  private readonly renderSettings = new RenderSettings();
  private readonly devPanel: DevPanel | null;

  private moveTimer = 0;
  private lastSent = { x: 0, y: 0, z: 0, yaw: 0, pitch: 0 };
  private fpsFrames = 0;
  private fpsTime = 0;

  constructor(
    profile: Profile,
    private readonly connection: Connection,
    init: InitMsg,
    library: BlockLibrary,
  ) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    // Harder filter than PCFSoft — less blurry / "dirty" block edges.
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    document.getElementById('app')!.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1500,
    );

    this.sun = setupSky(this.scene);
    this.scene.add(this.world.group);
    this.scene.add(this.remotes.group);

    // --- state from the server snapshot
    this.world.addCustomDefinitions([
      ...library.customBlocks,
      ...init.customBlocks,
    ]);
    this.world.addLibraryDefinitions([
      ...library.libraryBlocks,
      ...init.libraryBlocks,
    ]);
    this.world.selfId = init.selfId;
    this.world.zones = init.zones.map((z) => ({ ...z }));
    for (const [x, y, z, type, transform, viewId = 0, animOrigin = 0] of init.blocks) {
      this.world.applyOverride(x, y, z, type, transform, viewId, animOrigin);
    }
    for (const p of init.players) this.remotes.add(p);

    const self = init.self;
    const spawn = new THREE.Vector3(self.x, self.y, self.z);

    this.input = new Input(this.renderer.domElement);
    this.player = new PlayerController(this.world, this.input, spawn, {
      skinUrl: `/skins/${profile.skin}.png`,
      yaw: self.yaw,
      pitch: self.pitch,
    });
    this.lastSent = {
      x: self.x,
      y: self.y,
      z: self.z,
      yaw: self.yaw,
      pitch: self.pitch,
    };
    this.scene.add(this.player.puppet.group);

    this.signs = new Signs({
      claim: (zone) => this.connection.send({ t: 'claim', zone }),
      free: (zone) => this.connection.send({ t: 'free', zone }),
      toast: showToast,
    });
    this.signs.setState(this.world.zones, init.selfId);
    this.scene.add(this.signs.group);

    this.hotbar = new Hotbar(
      document.getElementById('hotbar')!,
      document.getElementById('inventory-popup')!,
      {
        initialTypes: library.hotbar,
        customBlocks: library.customBlocks,
        libraryBlocks: library.libraryBlocks,
        collections: library.collections,
        canvas: this.renderer.domElement,
        onChange: (types) => {
          void api.saveHotbar(types).catch(() => {
            showToast('Could not save quick-access palette');
          });
        },
        createCollection: (name, description, access) =>
          api.createCollection(name, description, access),
        updateCollection: (id, name, description, access) =>
          api.updateCollection(id, name, description, access),
        deleteCollection: (id, mode) => api.deleteCollection(id, mode),
        createSolidBlock: async (name, color, opacity, collision, glowIntensity) => {
          const definition = await api.createSolidBlock(
            name, color, opacity, collision, glowIntensity,
          );
          this.world.addLibraryDefinitions([definition]);
          return definition;
        },
        createTexturedBlock: async (name, color, pixels, noise, collision) => {
          const definition = await api.createTexturedBlock(
            name, color, pixels, noise, collision,
          );
          this.world.addLibraryDefinitions([definition]);
          this.hotbar.addLibraryBlock(definition);
          return definition;
        },
        createCustomBlock: async (name, gridSize, voxels, noise, collision) => {
          const definition = await api.createCustomBlock(
            name, gridSize, voxels, noise, collision,
          );
          this.world.addCustomDefinitions([definition]);
          this.hotbar.addCustomBlock(definition);
          return definition;
        },
        updateSolidBlock: async (id, patch) => {
          const definition = await api.updateSolidBlock(id, patch);
          this.world.addLibraryDefinitions([definition]);
          return definition;
        },
        updateTexturedBlock: async (
          id, name, color, pixels, collectionId, noise, collision, editViewId,
        ) => {
          const definition = await api.updateTexturedBlock(
            id, name, color, pixels, collectionId, noise, collision, editViewId,
          );
          this.world.addLibraryDefinitions([definition]);
          this.hotbar.updateLibraryBlock(definition);
          return definition;
        },
        deleteLibraryBlock: (id) => api.deleteLibraryBlock(id),
        patchCustomBlock: async (id, patch) => {
          const definition = await api.patchCustomBlock(id, patch);
          this.world.addCustomDefinitions([definition]);
          this.hotbar.updateCustomBlock(definition);
          return definition;
        },
        updateCustomBlock: async (
          id, name, gridSize, voxels, noise, collision, editViewId,
        ) => {
          const definition = await api.updateCustomBlock(
            id, name, gridSize, voxels, noise, collision, editViewId,
          );
          this.world.addCustomDefinitions([definition]);
          this.hotbar.updateCustomBlock(definition);
          return definition;
        },
        deleteCustomBlock: (id) => api.deleteCustomBlock(id),
        createCustomBlockView: async (id, name, sourceViewId) => {
          const definition = await api.createCustomBlockView(id, name, sourceViewId);
          this.world.addCustomDefinitions([definition]);
          this.hotbar.updateCustomBlock(definition);
          return definition;
        },
        activateCustomBlockView: async (id, viewId) => {
          const definition = await api.activateCustomBlockView(id, viewId);
          this.world.addCustomDefinitions([definition]);
          this.hotbar.updateCustomBlock(definition);
          return definition;
        },
        renameCustomBlockView: async (id, viewId, name) => {
          const definition = await api.renameCustomBlockView(id, viewId, name);
          this.world.addCustomDefinitions([definition]);
          this.hotbar.updateCustomBlock(definition);
          return definition;
        },
        deleteCustomBlockView: async (id, viewId) => {
          const definition = await api.deleteCustomBlockView(id, viewId);
          this.world.addCustomDefinitions([definition]);
          this.hotbar.updateCustomBlock(definition);
          return definition;
        },
        createLibraryBlockView: async (id, name, sourceViewId) => {
          const definition = await api.createLibraryBlockView(id, name, sourceViewId);
          this.world.addLibraryDefinitions([definition]);
          this.hotbar.updateLibraryBlock(definition);
          return definition;
        },
        activateLibraryBlockView: async (id, viewId) => {
          const definition = await api.activateLibraryBlockView(id, viewId);
          this.world.addLibraryDefinitions([definition]);
          this.hotbar.updateLibraryBlock(definition);
          return definition;
        },
        renameLibraryBlockView: async (id, viewId, name) => {
          const definition = await api.renameLibraryBlockView(id, viewId, name);
          this.world.addLibraryDefinitions([definition]);
          this.hotbar.updateLibraryBlock(definition);
          return definition;
        },
        deleteLibraryBlockView: async (id, viewId) => {
          const definition = await api.deleteLibraryBlockView(id, viewId);
          this.world.addLibraryDefinitions([definition]);
          this.hotbar.updateLibraryBlock(definition);
          return definition;
        },
        updateCustomBlockAnimation: async (id, animation) => {
          const definition = await api.updateCustomBlockAnimation(id, animation);
          this.world.addCustomDefinitions([definition]);
          this.hotbar.updateCustomBlock(definition);
          return definition;
        },
        updateLibraryBlockAnimation: async (id, animation) => {
          const definition = await api.updateLibraryBlockAnimation(id, animation);
          this.world.addLibraryDefinitions([definition]);
          this.hotbar.updateLibraryBlock(definition);
          return definition;
        },
      },
    );
    this.building = new BuildingSystem(
      this.world,
      this.player,
      {
        onBatch: (edits) => this.connection.send({ t: 'batch', edits }),
      },
      this.scene,
    );
    this.crafting = new CraftingSystem(this.world, this.scene, {
      canvas: this.renderer.domElement,
      save: async (name, gridSize, voxels, overwriteId) => {
        const definition = overwriteId === null
          ? await api.createCustomBlock(name, gridSize, voxels)
          : await api.updateCustomBlock(overwriteId, name, gridSize, voxels);
        this.world.addCustomDefinitions([definition]);
        if (overwriteId === null) this.hotbar.addCustomBlock(definition);
        else this.hotbar.updateCustomBlock(definition);
        return definition;
      },
      saveTexture: async (name, color, pixels, overwriteId) => {
        const definition = overwriteId === null
          ? await api.createTexturedBlock(name, color, pixels)
          : await api.updateTexturedBlock(overwriteId, name, color, pixels);
        this.world.addLibraryDefinitions([definition]);
        if (overwriteId === null) this.hotbar.addLibraryBlock(definition);
        else this.hotbar.updateLibraryBlock(definition);
        return definition;
      },
    });
    this.faceUi = new FaceUiSystem(this.scene);
    this.faceUi.registerDefinition(TRANSFORM_FACE_UI);
    this.faceUi.registerDefinition(CRAFT_FACE_UI);
    this.faceUi.registerBinding({
      definitionId: TRANSFORM_FACE_UI_ID,
      visibility: 'focused',
      matches: isTransformableBlock,
      onActivate: (buttonId, layoutId, _modifiers, _target, axes) => {
        const action = transformActionForZone(buttonId, layoutId, axes);
        if (action) {
          this.building.transformFocusedBlock(
            action.axis,
            action.mirror,
            action.direction,
          );
        }
      },
    });
    this.faceUi.registerBinding({
      definitionId: TRANSFORM_FACE_UI_ID,
      visibility: 'focused',
      matches: (type) => type === TEXTURE_DRAFT_FACE_TYPE,
      onActivate: (buttonId, layoutId, _modifiers, _target, axes) => {
        const action = transformActionForZone(buttonId, layoutId, axes);
        if (action) {
          this.crafting.transformTextureDraft(
            action.axis,
            action.mirror,
            action.direction,
          );
        }
      },
    });
    this.faceUi.registerBinding({
      definitionId: CRAFT_FACE_UI_ID,
      visibility: 'focused',
      matches: (type) => type === CRAFT_BLOCK_TYPE,
      onActivate: (buttonId, _layoutId, _modifiers, target) => {
        this.crafting.requestFaceAction(
          buttonId as CraftFaceAction,
          target,
        );
      },
    });
    this.tools = new ToolsPalette(
      document.getElementById('tools-palette')!,
      {
        canvas: this.renderer.domElement,
        onToolChange: () => this.building.cancelDestroy(),
      },
    );

    this.devPanel = profile.isDev
      ? new DevPanel(document.getElementById('dev-popup')!, this.renderSettings, {
        canvas: this.renderer.domElement,
      })
      : null;
    if (this.devPanel) {
      this.renderSettings.subscribe((state) => this.applyRenderSettings(state));
      this.applyRenderSettings(this.renderSettings.get());
      const overlayHint = document.querySelector('#overlay .hint');
      if (overlayHint) {
        overlayHint.appendChild(document.createElement('br'));
        const kbd = document.createElement('kbd');
        kbd.textContent = 'F3';
        overlayHint.append(kbd, ' — developer render panel');
      }
    }

    this.connection.onMessage = (msg) => this.onServerMessage(msg);
    this.connection.onClose = (event) => {
      showToast(event.code === 4000
        ? 'This game session was moved to a newer tab'
        : 'Connection lost - reloading...');
      setTimeout(() => location.reload(), 1500);
    };

    const overlay = document.getElementById('overlay')!;
    overlay.addEventListener('click', () => this.input.requestLock());
    this.input.onLockChange((locked) => overlay.classList.toggle('hidden', locked));
    let escapeResumeArmed = false;
    document.addEventListener('keydown', (event) => {
      if (
        event.code === 'Escape' &&
        !event.repeat &&
        !this.input.isLocked &&
        !overlay.classList.contains('hidden') &&
        !document.getElementById('inventory-popup')!.classList.contains('visible') &&
        !document.getElementById('dev-popup')!.classList.contains('visible') &&
        !document.getElementById('craft-dialog')!.classList.contains('visible') &&
        !this.tools.isExpanded
      ) {
        event.preventDefault();
        escapeResumeArmed = true;
      }
    });
    document.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape' || !escapeResumeArmed) return;
      escapeResumeArmed = false;
      event.preventDefault();
      if (!this.input.isLocked) this.input.requestLock();
    });

    // Back to the lobby: a reload tears the game down; the session survives.
    document.getElementById('exit-world')!.addEventListener('click', (e) => {
      e.stopPropagation();
      this.connection.onClose = null;
      location.reload();
    });

    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });

    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__game = {
        player: this.player,
        camera: this.camera,
        scene: this.scene,
        world: this.world,
        input: this.input,
        hotbar: this.hotbar,
        building: this.building,
        crafting: this.crafting,
        tools: this.tools,
        faceUi: this.faceUi,
        connection: this.connection,
        signs: this.signs,
      };
    }
  }

  start(): void {
    this.renderer.setAnimationLoop(() => this.tick());
  }

  private applyRenderSettings(state: RenderSettingsState): void {
    this.renderer.shadowMap.enabled = state.shadows;
    this.sun.castShadow = state.shadows;
    const res = state.shadowResolution;
    if (
      this.sun.shadow.mapSize.x !== res
      || this.sun.shadow.mapSize.y !== res
    ) {
      this.sun.shadow.mapSize.set(res, res);
      this.sun.shadow.map?.dispose();
      this.sun.shadow.map = null;
    }
    this.renderer.shadowMap.needsUpdate = true;
    this.world.setGlowEnabled(state.glow);
    this.world.setRenderDistance(state.renderDistance);
    this.world.setAnimationFlags(state.animations);
    if (this.scene.fog instanceof THREE.Fog) {
      this.scene.fog.near = state.renderDistance * 0.72;
      this.scene.fog.far = state.renderDistance;
    }
  }

  private tick(): void {
    // Clamp dt so a backgrounded tab doesn't produce a huge physics step.
    const dt = Math.min(this.clock.getDelta(), 0.05);

    this.player.update(dt, this.camera);
    this.world.setAnimationTick(currentWorldAnimTick());
    this.world.updateRenderDistance(this.player.position);
    this.remotes.update(dt);

    const buildingMode = this.input.isLocked && this.player.isFirstPerson;
    const buildTool = this.tools.activeTool === 'build';
    const transformTool = this.tools.activeTool === 'transform';
    this.building.update(
      this.camera,
      buildingMode,
      dt,
      this.input.isButtonDown(0),
      this.input.control,
      this.input.alt,
      this.hotbar.selectedBlockId,
      transformTool,
    );
    this.crafting.update(
      this.camera,
      buildingMode && (buildTool || transformTool),
      dt,
      this.input.isButtonDown(0),
      this.input.control,
      this.input.alt,
      this.hotbar.selectedStandardVoxelType,
      this.hotbar.selectedLibraryBlock,
      this.hotbar.selectedSolidColor,
      this.building.currentHit,
    );
    const faceHit = (transformTool || buildTool) ? this.building.currentHit : null;
    const faceType = faceHit
      ? this.world.getBlock(faceHit.x, faceHit.y, faceHit.z)
      : 0;
    const transformTarget =
      buildingMode &&
      transformTool &&
      faceHit &&
      isTransformableBlock(faceType) &&
      this.world.canEditAt(faceHit.x, faceHit.z)
        ? faceHit
        : null;
    const textureDraftTarget =
      buildingMode && transformTool
        ? this.crafting.currentTextureFaceTarget
        : null;
    const craftTarget =
      buildingMode &&
      buildTool &&
      faceHit &&
      faceType === CRAFT_BLOCK_TYPE &&
      this.world.canEditAt(faceHit.x, faceHit.z)
        ? faceHit
        : null;
    const faceTarget = textureDraftTarget ?? transformTarget ?? craftTarget;
    const focusedFaceType = textureDraftTarget ? TEXTURE_DRAFT_FACE_TYPE : faceType;
    this.faceUi.updateFocused(
      this.camera,
      faceTarget,
      faceTarget ? focusedFaceType : 0,
    );
    this.building.suppressVisuals(
      this.crafting.hasTargetHighlight || this.faceUi.hasFocusedUi,
    );

    for (const button of this.input.consumeClicks()) {
      if (
        buildingMode &&
        this.faceUi.hasFocusedUi &&
        this.faceUi.handleClick(this.camera, button, {
          control: this.input.control,
          alt: this.input.alt,
          shift: this.input.sprint,
        })
      ) {
        this.building.cancelDestroy();
        continue;
      }
      if (this.signs.tryClick(this.camera, button)) {
        this.building.cancelDestroy();
        continue;
      }
      if (
        buildingMode &&
        buildTool &&
        this.crafting.handleClick(
          button,
          this.hotbar.selectedStandardVoxelType,
          this.hotbar.selectedCustomBlock,
          this.hotbar.selectedLibraryBlock,
          this.hotbar.selectedSolidColor,
          this.input.control,
          this.input.alt,
        )
      ) {
        this.building.cancelDestroy();
        continue;
      }
      if (buildingMode && buildTool) {
        this.building.handleClick(
          button,
          this.hotbar,
          this.input.control,
          this.input.alt,
        );
      }
    }
    if (this.input.consumeUndo()) this.building.undo();
    if (this.input.consumeRedo()) this.building.redo();

    this.sendMovement(dt);

    const crosshair = document.getElementById('crosshair')!;
    crosshair.style.display = buildingMode ? '' : 'none';
    this.hotbar.setVisible(buildingMode && buildTool);
    this.tools.setVisible(buildingMode);

    // Keep the shadow camera centered on the player.
    this.sun.position.copy(this.player.position).add(SUN_OFFSET);
    this.sun.target.position.copy(this.player.position);

    this.updateHud(dt);
    this.renderer.render(this.scene, this.camera);
  }

  private sendMovement(dt: number): void {
    this.moveTimer += dt;
    if (this.moveTimer < MOVE_SEND_INTERVAL) return;
    this.moveTimer = 0;

    const p = this.player.position;
    const { yaw, pitch } = this.player.view;
    const s = this.lastSent;
    if (
      Math.abs(p.x - s.x) < 1e-3 &&
      Math.abs(p.y - s.y) < 1e-3 &&
      Math.abs(p.z - s.z) < 1e-3 &&
      Math.abs(yaw - s.yaw) < 1e-3 &&
      Math.abs(pitch - s.pitch) < 1e-3
    ) {
      return;
    }
    this.lastSent = { x: p.x, y: p.y, z: p.z, yaw, pitch };
    this.connection.send({ t: 'move', x: p.x, y: p.y, z: p.z, yaw, pitch });
  }

  private updateHud(dt: number): void {
    this.fpsFrames++;
    this.fpsTime += dt;
    if (this.fpsTime < 0.5) return;
    const fps = Math.round(this.fpsFrames / this.fpsTime);
    this.fpsFrames = 0;
    this.fpsTime = 0;
    const p = this.player.position;
    document.getElementById('debug')!.textContent =
      `${fps} fps\n` +
      `x ${p.x.toFixed(1)}  y ${p.y.toFixed(1)}  z ${p.z.toFixed(1)}\n` +
      `${this.player.isFirstPerson ? '1st' : '3rd'} person` +
      (this.player.isFlying ? '  |  flying' : '');
  }

  private onServerMessage(msg: ServerMsg): void {
    switch (msg.t) {
      case 'join':
        this.remotes.add(msg.player);
        showToast(`${msg.player.nick} joined`);
        break;
      case 'leave':
        this.remotes.remove(msg.id);
        break;
      case 'move':
        this.remotes.setTarget(msg.id, msg.x, msg.y, msg.z, msg.yaw, msg.pitch);
        break;
      case 'place':
        this.world.setBlock(msg.x, msg.y, msg.z, msg.type);
        break;
      case 'remove':
        this.world.removeBlock(msg.x, msg.y, msg.z);
        break;
      case 'batch':
        if (msg.customBlocks) this.world.addCustomDefinitions(msg.customBlocks);
        if (msg.libraryBlocks) this.world.addLibraryDefinitions(msg.libraryBlocks);
        for (const [x, y, z, type, transform, viewId = 0, animOrigin = 0] of msg.edits) {
          this.world.setBlockState(x, y, z, type, transform, viewId, animOrigin);
        }
        break;
      case 'customBlock':
        this.world.addCustomDefinitions([msg.definition]);
        break;
      case 'libraryBlock':
        this.world.addLibraryDefinitions([msg.definition]);
        this.hotbar.updateLibraryBlock(msg.definition);
        break;
      case 'zone': {
        const zone = {
          index: msg.index,
          ownerId: msg.ownerId,
          ownerNick: msg.ownerNick,
        };
        this.world.zones[msg.index] = zone;
        this.signs.updateZone(zone);
        if (msg.cleared) {
          this.world.clearZone(msg.index);
          this.building.clearHistory();
        }
        if (msg.ownerId === this.world.selfId) {
          showToast('Zone claimed - happy building!');
        }
        break;
      }
      case 'error':
        showToast(msg.message);
        break;
    }
  }
}

function isTransformableBlock(type: number): boolean {
  return customBlockId(type) !== null || libraryBlockId(type) !== null;
}
